import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { RegionCodes, getRegionCode } from './helpers/RegionCodes';

export type AppSettings = {
  AuthenticationServiceUrl: string;
  AuthClientId: string;
  AuthClientSecret: string;
  APIMUrl: string;
  CmsEnvironment: string;
  Environment: string;
  GoogleMapsApiKey: string;
  SegmentApiKey: string;
  SiftBeaconKey: string;
  RecurlyPublicKey: string;
  NotificationServiceUrl: string;
  Raygun: RaygunSettings;
  CMSAssetBaseUrl: string;
  ApimOverrides: GoFormzServiceNames;
  ServiceBusOverrides: GoFormzServiceNames;
  RoutingEnvironment: string;
  SetBackendServiceUrlHeaders: GoFormzServiceNames;
  Routes: GoFormzServiceNames;
  Apps: GoFormzAppNames;
  HipaaSetupKey: string;
  PublicForms: PublicForms;
};

export type RaygunSettings = {
  ApiKey: string;
};

export type GoFormzServiceNames = {
  AccountService: string;
  AIService: string;
  AnalyticsService: string;
  Api: string;
  ApimService: string;
  ApplicationService: string;
  AppsApimService: string;
  AuditService: string;
  AuthenticationService: string;
  BillingService: string;
  CmsService: string;
  DataSourceBlobService: string;
  DataSourceService: string;
  DeviceService: string;
  EmailService: string;
  FileService: string;
  IntegrationHub: string;
  NotificationService: string;
  PdfService: string;
  PublicFormsService: string;
  SchedulerService: string;
  SyncService: string;
  TemplateService: string;
  WebhookService: string;
  WorkflowService: string;
};

export type GoFormzAppNames = {
  Acumatica: string;
  Alfresco: string;
  AWS: string;
  AzureBlobStorage: string;
  AzureEventGrid: string;
  BIM360: string;
  Box: string;
  CardConnect: string;
  CustomApp: string;
  DocuWare: string;
  Dropbox: string;
  Dynamics365: string;
  Egnyte: string;
  Fivetran: string;
  FullContact: string;
  GoFormzAI: string;
  GoFormzServices: string;
  GoFormzUtilities: string;
  GoogleBigQuery: string;
  GoogleCloudStorage: string;
  GoogleDrive: string;
  Microsoft: string;
  MicrosoftOffice365: string;
  NetSuite: string;
  Plangrid: string;
  PowerBI: string;
  Procore: string;
  QuickBooksOnline: string;
  Salesforce: string;
  ServiceM8: string;
  SharePoint: string;
  Slack: string;
  SmartSheet: string;
  Snowflake: string;
  SqlServer: string;
  Twilio: string;
};

type PublicForms = {
  DefaultIncreasedPublicFormLimit: number;
};

let appSettings: AppSettings = null;

async function LoadProductionAppSettings() {
  const emptyConfig = {};
  const regionCode = getRegionCode();
  const prefix = regionCode ? `.${regionCode.toLowerCase()}` : `.${RegionCodes.US}`;
  const path = `${window.resourceBasePath || '/'}appsettings${prefix}.json?random=${uuidv4()}`;
  const appSettingsProduction = await axios(path, { method: 'GET' });
  if (!appSettingsProduction || !appSettingsProduction.data) {
    return emptyConfig;
  }

  const sanitizeProperties = (obj) => {
    if (!obj) {
      return;
    }

    for (const [key, value] of Object.entries(obj)) {
      if (!value) {
        delete obj[key];
      }
    }
  };

  sanitizeProperties(appSettingsProduction.data?.Routes);

  return appSettingsProduction.data;
}

async function LoadDevelopmentAppSettings() {
  const emptyConfig = {};
  if (process.env.NODE_ENV === 'production') {
    return emptyConfig;
  }

  const path = `${window.resourceBasePath || '/'}appsettings.development.json?random=${uuidv4()}`;
  const appSettingsDevelopment = await axios(path, { method: 'GET' });
  if (!appSettingsDevelopment || !appSettingsDevelopment.data) {
    return emptyConfig;
  }

  //translate machine name to local machine name
  if (appSettingsDevelopment.data.RoutingEnvironment === '{MACHINE_NAME}') {
    appSettingsDevelopment.data.RoutingEnvironment = process.env.HOST_NAME;
  }

  const sanitizeProperties = (obj, removeEmpty = true) => {
    if (!obj) {
      return;
    }

    for (const [key, value] of Object.entries(obj)) {
      if (key.startsWith('Disabled.') || key.startsWith('__COMMENT__.') || (removeEmpty && !value)) {
        delete obj[key];
      }
    }
  };

  sanitizeProperties(appSettingsDevelopment.data, false);
  sanitizeProperties(appSettingsDevelopment.data?.ApimOverrides);
  sanitizeProperties(appSettingsDevelopment.data?.ServiceBusOverrides, false);
  sanitizeProperties(appSettingsDevelopment.data?.Apps);
  sanitizeProperties(appSettingsDevelopment.data?.SetBackendServiceUrlHeaders);

  return appSettingsDevelopment.data;
}

export async function LoadAppSettings() {
  const appSettingsProduction = await LoadProductionAppSettings();
  const appSettingsDevelopment = await LoadDevelopmentAppSettings();
  appSettings = {
    ...appSettingsProduction,
    ...appSettingsDevelopment
  };
}

export function GetAppSettings(): AppSettings {
  // return a copy
  return { ...appSettings };
}

export const GoFormzServices: GoFormzServiceNames = {
  AccountService: 'AccountService',
  AIService: 'AIService',
  AnalyticsService: 'AnalyticsService',
  Api: 'Api',
  ApimService: 'ApimService',
  ApplicationService: 'ApplicationService',
  AppsApimService: 'AppsApimService',
  AuditService: 'AuditService',
  AuthenticationService: 'AuthenticationService',
  BillingService: 'BillingService',
  CmsService: 'CmsService',
  DataSourceBlobService: 'DataSourceBlobService',
  DataSourceService: 'DataSourceService',
  DeviceService: 'DeviceService',
  EmailService: 'EmailService',
  FileService: 'FileService',
  IntegrationHub: 'IntegrationHub',
  NotificationService: 'NotificationService',
  PdfService: 'PdfService',
  PublicFormsService: 'PublicFormsService',
  SchedulerService: 'SchedulerService',
  SyncService: 'SyncService',
  TemplateService: 'TemplateService',
  WebhookService: 'WebhookService',
  WorkflowService: 'WorkflowService'
};

export const GoFormzApps: GoFormzAppNames = {
  Acumatica: 'Acumatica',
  Alfresco: 'Alfresco',
  AWS: 'AWS',
  AzureBlobStorage: 'AzureBlobStorage',
  AzureEventGrid: 'AzureEventGrid',
  BIM360: 'BIM360',
  Box: 'Box',
  CardConnect: 'CardConnect',
  CustomApp: 'CustomApp',
  DocuWare: 'DocuWare',
  Dropbox: 'Dropbox',
  Dynamics365: 'Dynamics365',
  Egnyte: 'Egnyte',
  Fivetran: 'Fivetran',
  FullContact: 'FullContact',
  GoFormzAI: 'GoFormzAI',
  GoFormzServices: 'GoFormzServices',
  GoFormzUtilities: 'GoFormzUtilities',
  GoogleBigQuery: 'GoogleBigQuery',
  GoogleCloudStorage: 'GoogleCloudStorage',
  GoogleDrive: 'GoogleDrive',
  Microsoft: 'Microsoft',
  MicrosoftOffice365: 'MicrosoftOffice365',
  NetSuite: 'NetSuite',
  Plangrid: 'Plangrid',
  PowerBI: 'PowerBI',
  Procore: 'Procore',
  QuickBooksOnline: 'QuickBooksOnline',
  Salesforce: 'Salesforce',
  ServiceM8: 'ServiceM8',
  SharePoint: 'SharePoint',
  Slack: 'Slack',
  SmartSheet: 'SmartSheet',
  Snowflake: 'Snowflake',
  SqlServer: 'SqlServer',
  Twilio: 'Twilio'
};
